import React, { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { DataTable } from "@/components/ui/data-table";
import { Badge } from "@/components/ui/badge";
import {
  Plus,
  RotateCcw,
  Calendar,
  Search,
  MoreHorizontal,
  FileText,
  Activity,
} from "lucide-react";
import { Link } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { ProgramTableSkeleton } from "@/components/ui/program-table-skeleton";
import { formatDistanceToNow, addDays, startOfDay, endOfDay } from "date-fns";

import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { toast } from "sonner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetClose,
} from "@/components/ui/sheet";
import { ProgramApi } from "@/api";
import { getFormattedDate, getFormattedDateTime } from "@/utils";
import { EnhancedPaginationControl } from "@/components/common/EnhancedPaginationControl";
import { PAGE_SIZE } from "@/constants/common.constant";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogAction,
  AlertDialogCancel,
} from "@/components/ui/alert-dialog";
import { DocumentApi } from "@/api/document.api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
// Import permission components and constants
import { PermissionGuard } from "@/components/common/PermissionGuard";
import { PERMISSION_CODES, FEATURE_CODES } from "@/constants/permissions";

const filters = {
  pageSize: PAGE_SIZE,
  programCode: null,
  programName: null,
  programType: "TRANSFER",
  sortBy: "createdDate",
  sortOrder: "desc",
};

const DELAY = 500;

// Helper to get start and end in millis for a given range
const getDateRangeMillis = (range) => {
  const now = new Date();
  let start, end;
  switch (range) {
    case "30days":
      start = startOfDay(addDays(now, -29));
      end = endOfDay(now);
      break;
    case "7days":
      start = startOfDay(addDays(now, -6));
      end = endOfDay(now);
      break;
    case "90days":
      start = startOfDay(addDays(now, -89));
      end = endOfDay(now);
      break;
    case "all":
      start = null;
      end = null;
      break;
    default:
      start = null;
      end = null;
  }
  return {
    startDateInMillis: range === "all" ? null : start ? start.getTime() : null,
    endDateInMillis: range === "all" ? null : end ? end.getTime() : null,
  };
};

const TranferPrograms = () => {
  const [searchType, setSearchType] = useState("programName"); // 'programName' or 'programCode'
  const [searchQuery, setSearchQuery] = useState("");
  const [inputValue, setInputValue] = useState("");
  const [dateRange, setDateRange] = useState("all");
  const [filteredPrograms, setFilteredPrograms] = useState<any[]>([]);
  const [totalPrograms, setTotalPrograms] = useState(0);
  const [page, setPage] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const totalPages = Math.ceil(totalPrograms / rowsPerPage);
  const debounceTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [programToDelete, setProgramToDelete] = useState<any | null>(null);
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [activityLogs, setActivityLogs] = useState([]);
  const [activityLoading, setActivityLoading] = useState(false);
  const [activityError, setActivityError] = useState(null);
  const [activityTab, setActivityTab] = useState("documents");
  const [programCodeQuery, setProgramCodeQuery] = useState("");

  function debounce<T extends (...args: any[]) => void>(
    func: T,
    delay: number
  ) {
    return function (...args: Parameters<T>) {
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current);
      }
      debounceTimeout.current = setTimeout(() => {
        func(...args);
        debounceTimeout.current = null;
      }, delay);
    };
  }

  async function fetchPrograms(filters = {}) {
    try {
      setIsLoading(true);

      // Get date range millis for API call
      const dateRangeData = getDateRangeMillis(dateRange);

      // Include current search parameters
      const searchParams = searchQuery ? { [searchType]: searchQuery } : {};

      const { data } = await ProgramApi.fetchProgram({
        ...filters,
        ...searchParams,
        startDateInMillis: dateRangeData.startDateInMillis,
        endDateInMillis: dateRangeData.endDateInMillis,
      });
      setTotalPrograms(data?.totalNumberOfPrograms || 0);

      const programs = data?.programVOs?.map((program) => ({
        id: program?.id,
        name: program?.programName,
        pointsName: program?.loyaltyCurrencyId || "-",
        city: program?.city || "-",
        country: program?.country || "-",
        programCode: program?.programCode,
        currency: program?.currencyVO?.iso3CurrencyCode,
        costPerPoint: program?.costPerPoint,
        merchants: 0,
        status: "active",
        created: program?.createdDateInMillis
          ? getFormattedDateTime(program?.createdDateInMillis)
          : "",
        logo: program?.logo,
        programLogoUri: program?.programLogoUri,
        invoiceFrequencyInDays: program?.invoiceFrequencyInDays,
      }));
      setFilteredPrograms(programs || []);
    } catch (err) {
      toast.error("Error in fetching programs");
    } finally {
      setIsLoading(false);
    }
  }
  useEffect(() => {
    fetchPrograms({
      ...filters,
      pageNumber: page,
      pageSize: rowsPerPage,
    });
  }, [page, rowsPerPage, dateRange, searchQuery, searchType]);

  const resetFilters = () => {
    setSearchQuery("");
    setInputValue("");
    setSearchType("programName");
    setDateRange("all");
    setPage(0);
    fetchPrograms({ ...filters, pageNumber: 0 });
  };

  const debouncedSearch = debounce((query) => {
    setSearchQuery(query);
    setPage(0);
  }, DELAY);

  // Function to format date as relative time (e.g., "1 day ago")
  const formatRelativeTime = (dateString: string) => {
    return formatDistanceToNow(new Date(dateString), { addSuffix: true });
  };

  // Fetch documents for selected program
  useEffect(() => {
    async function fetchDocuments() {
      if (!selectedProgram?.id) return;
      try {
        const { data } = await DocumentApi.getDocumentsForProgram(
          selectedProgram.id
        );
        setDocuments(data || []);
      } catch (err) {
        setDocuments([]);
      }
    }
    fetchDocuments();
  }, [selectedProgram?.id]);

  // Fetch activity logs for selected program
  useEffect(() => {
    async function fetchActivity() {
      setActivityLoading(true);
      setActivityError(null);
      try {
        if (!selectedProgram?.programCode) return;
        const payload = {
          id: null,
          programCode: selectedProgram.programCode,
          merchantCode: null,
          pageNumber: 0,
          pageSize: 50,
          sortOrder: "desc",
          sortBy: "createdAt",
        };
        const API_BASE = import.meta.env.VITE_REACT_APP_CORE_ENGINE_URL;
        const res = await fetch(`${API_BASE}/activity/getActivityLogs`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify(payload),
        });
        const data = await res.json();
        setActivityLogs(data.activityLogVOs || []);
      } catch (err) {
        setActivityError("Failed to fetch activity logs");
      } finally {
        setActivityLoading(false);
      }
    }
    if (activityTab === "activity" && selectedProgram?.programCode) {
      fetchActivity();
    }
  }, [activityTab, selectedProgram?.programCode]);

  return (
    <div className="space-y-8">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">
            Transfer Partners
          </h1>
          <p className="text-gray-500 mt-1">
            Manage your transfer partners and their attributes
          </p>
        </div>
        <div className="flex gap-3">
          <PermissionGuard permissionCode={PERMISSION_CODES.CREATE_PROGRAM}>
            <Button className="bg-blue-600 hover:bg-blue-700" asChild>
              <Link to="/xpandpoint/partner/create">
                <Plus className="h-4 w-4 mr-2" /> Add Transfer Partner
              </Link>
            </Button>
          </PermissionGuard>
        </div>
      </div>

      {/* Filter section */}
      {true && (
        <div className="flex flex-col sm:flex-row gap-4 items-end sm:items-center bg-white p-4 rounded-lg border border-gray-100 shadow-sm">
          <div className="flex-1 flex gap-2 items-end">
            <div>
              <Select
                value={searchType}
                onValueChange={(value) => {
                  setSearchType(value);
                  setInputValue("");
                  setSearchQuery("");
                }}
              >
                <SelectTrigger className="w-36">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="programName">Partner Name</SelectItem>
                  <SelectItem value="programCode">Partner Code</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-500" />
                <Input
                  id="search"
                  placeholder={`Search by ${
                    searchType === "programName"
                      ? "partner name"
                      : "partner code"
                  }...`}
                  className="pl-9"
                  value={inputValue}
                  onChange={(e) => {
                    const query = e.target.value;
                    setInputValue(query); // Update UI immediately for responsive typing
                    debouncedSearch(query); // Debounced API call
                  }}
                />
              </div>
            </div>
          </div>

          <div className="w-full sm:w-auto">
            <Select
              value={dateRange}
              onValueChange={(value) => {
                setDateRange(value);
                setPage(0); // Reset to first page when date range changes
              }}
              disabled={isLoading}
            >
              <SelectTrigger id="dateRange" className="w-full sm:w-[180px]">
                <SelectValue placeholder="Select a time period" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Time</SelectItem>
                <SelectItem value="7days">Last 7 Days</SelectItem>
                <SelectItem value="30days">Last 30 Days</SelectItem>
                <SelectItem value="90days">Last 90 Days</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <Button
            variant="outline"
            size="sm"
            className="flex items-center gap-2 px-3 py-2 text-sm"
            onClick={resetFilters}
            disabled={isLoading}
          >
            <RotateCcw className="h-4 w-4" />
            Reset
          </Button>
        </div>
      )}

      <DataTable
        title="All Transfer Partners"
        data={isLoading ? [] : filteredPrograms}
        columns={[
          {
            header: "Created Date",
            accessorKey: "created",
          },
          {
            header: "Logo",
            accessorKey: "programLogoUri",
            cell: (row) => (
              <div className="flex items-center">
                <img
                  src={
                    row.programLogoUri
                      ? `${import.meta.env.VITE_REACT_APP_CORE_ENGINE_URL}${
                          row.programLogoUri
                        }`
                      : "/placeholder.svg"
                  }
                  alt={row.name}
                  className="h-10 w-10 object-contain rounded-md"
                  onError={(e) => {
                    e.currentTarget.onerror = null;
                    e.currentTarget.src = "/placeholder.svg";
                  }}
                />
              </div>
            ),
          },
          {
            header: "Code",
            accessorKey: "programCode",
          },
          {
            header: "Name",
            accessorKey: "name",
          },
          {
            header: "Currency",
            accessorKey: "currency",
          },
          {
            header: "CPP",
            accessorKey: "costPerPoint",
            cell: (row) => row.costPerPoint ?? "-",
          },
          {
            header: "Invoice Frequency",
            accessorKey: "invoiceFrequencyInDays",
            cell: (row) =>
              row.invoiceFrequencyInDays
                ? `${row.invoiceFrequencyInDays} days`
                : "-",
          },
          {
            header: "City/Country",
            accessorKey: "cityCountry",
            cell: (row) => (
              <div>
                <div>{row.city}</div>
                <div className="text-xs text-gray-500 mt-0.5">
                  {row.country}
                </div>
              </div>
            ),
          },
          {
            header: "Actions",
            id: "actions",
            cell: (row) => (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                    <span className="sr-only">Open menu</span>
                    <MoreHorizontal size={16} />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="end"
                  className="bg-white min-w-[8rem] p-1"
                >
                  <DropdownMenuItem asChild>
                    <Link to={`/xpandpoint/partners/${row.id}`}>
                      {/* <Link to={`/xpandpoint/programs/${row.id}`}> */}
                      View Details
                    </Link>
                  </DropdownMenuItem>
                  {/* <PermissionGuard
                    permissionCode={PERMISSION_CODES.DELETE_PROGRAM}
                  >
                    <DropdownMenuItem
                      onClick={() => {
                        setProgramToDelete(row);
                        setDeleteDialogOpen(true);
                      }}
                      className="text-red-600"
                    >
                      Delete Program
                    </DropdownMenuItem>
                  </PermissionGuard> */}
                </DropdownMenuContent>
              </DropdownMenu>
            ),
          },
        ]}
        customTableBody={isLoading ? <ProgramTableSkeleton /> : undefined}
      />
      {!isLoading && totalPages ? (
        <div className="flex items-center justify-between mt-4">
          <div className="flex items-center space-x-2">
            <span className="text-sm text-gray-500">Rows per page:</span>
            <Select
              value={rowsPerPage.toString()}
              onValueChange={(value) => {
                setRowsPerPage(Number(value));
                setPage(0);
              }}
            >
              <SelectTrigger className="w-[80px]">
                <SelectValue placeholder={rowsPerPage.toString()} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="10">10</SelectItem>
                <SelectItem value="20">20</SelectItem>
                <SelectItem value="50">50</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <EnhancedPaginationControl
            totalPages={totalPages}
            currentPage={page}
            onPageChange={(page) => setPage(page)}
          />
          <div className="w-[160px]" /> {/* Spacer to balance layout */}
        </div>
      ) : null}
      {/* Delete Program Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Program</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete the program "
              {programToDelete?.name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              onClick={() => {
                // TODO: Implement actual delete logic here
                // deleteProgram(programToDelete?.id);
                setDeleteDialogOpen(false);
                setProgramToDelete(null);
                toast.success("Program deleted successfully");
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      {/* Documents & Activity Modal/Panel */}
      {selectedProgram && (
        <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg max-w-3xl w-full p-6 relative">
            <Button
              className="absolute top-2 right-2"
              size="icon"
              variant="ghost"
              onClick={() => setSelectedProgram(null)}
            >
              ×
            </Button>
            <h2 className="text-xl font-bold mb-4">
              {selectedProgram.name} - Documents & Activity
            </h2>
            <Tabs
              value={activityTab}
              onValueChange={setActivityTab}
              className="w-full"
            >
              <TabsList className="bg-white border-b border-gray-200 w-full justify-start rounded-none h-12 px-6">
                <TabsTrigger
                  value="documents"
                  className="data-[state=active]:border-b-2 data-[state=active]:border-blue-600 rounded-none"
                >
                  Documents
                </TabsTrigger>
                <TabsTrigger
                  value="activity"
                  className="data-[state=active]:border-b-2 data-[state=active]:border-blue-600 rounded-none"
                >
                  Activity
                </TabsTrigger>
              </TabsList>
              <TabsContent value="documents" className="space-y-6 pt-4">
                <Card className="border-0 shadow-sm bg-white">
                  <CardHeader className="border-b border-gray-100 bg-gray-50 flex flex-row items-center justify-between">
                    <CardTitle className="text-lg font-medium text-gray-800">
                      Program Documents
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-6">
                    {documents.length === 0 ? (
                      <div className="text-center py-8 text-gray-500">
                        <p>No documents available.</p>
                      </div>
                    ) : (
                      <div className="divide-y divide-gray-100">
                        {documents.map((doc) => (
                          <div
                            key={doc.id}
                            className="py-4 flex flex-col sm:flex-row sm:items-center justify-between first:pt-0 last:pb-0"
                          >
                            <div className="flex items-center">
                              <span className="mr-3 text-blue-500">
                                <FileText className="h-5 w-5" />
                              </span>
                              <div>
                                <p
                                  className="font-medium text-gray-800 truncate max-w-xs"
                                  title={doc.documentName}
                                >
                                  {doc.documentName}
                                </p>
                                <p className="text-xs text-gray-500">
                                  Uploaded by {doc.uploadedBy} •
                                  {doc.fileSize >= 10240
                                    ? `${(doc.fileSize / (1024 * 1024)).toFixed(
                                        2
                                      )} MB`
                                    : `${doc.fileSize} bytes`}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center mt-2 sm:mt-0">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  /* TODO: Download logic */
                                }}
                              >
                                Download
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
              <TabsContent value="activity" className="space-y-6 pt-4">
                <Card className="border-0 shadow-sm bg-white">
                  <CardHeader className="border-b border-gray-100 bg-gray-50">
                    <CardTitle className="text-lg font-medium text-gray-800">
                      Activity Log
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-6">
                    {activityLoading ? (
                      <div className="text-center text-gray-500">
                        Loading activity...
                      </div>
                    ) : activityError ? (
                      <div className="text-center text-red-500">
                        {activityError}
                      </div>
                    ) : activityLogs.length === 0 ? (
                      <div className="text-center text-gray-500">
                        No activity found.
                      </div>
                    ) : (
                      <div className="divide-y divide-gray-100">
                        {activityLogs.map((log) => (
                          <div
                            key={log.id}
                            className="py-4 first:pt-0 last:pb-0"
                          >
                            <div className="flex items-start gap-3">
                              <div className="p-2 rounded-md bg-blue-50 text-blue-600">
                                <Activity className="h-4 w-4" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <div
                                  className="text-gray-800 font-medium text-base mb-1 truncate"
                                  title={log.description}
                                >
                                  {log.description}
                                </div>
                                <div className="flex flex-wrap gap-2 items-center text-xs text-gray-500">
                                  <span title={log.actorName}>
                                    {log.actorName}
                                  </span>
                                  <span className="bg-gray-100 text-gray-600 px-2 py-0.5 rounded">
                                    {log.action
                                      .replace(/_/g, " ")
                                      .toLowerCase()
                                      .replace(/\b\w/g, (l) => l.toUpperCase())}
                                  </span>
                                  <span className="text-gray-400">
                                    {log.createdAt
                                      ? formatRelativeTime(log.createdAt)
                                      : ""}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </div>
        </div>
      )}
    </div>
  );
};

export default TranferPrograms;
